import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Download,
  ExternalLink,
  FileUp,
  Loader2,
  SearchCheck,
  ShieldQuestion,
} from "lucide-react";

import { useToast } from "../../contexts/ToastContext";
import api from "../../services/api";

const STATUS_OPTIONS = [
  {
    value: "not_evaluated",
    label: "Não avaliado",
    className: "bg-gray-100 text-gray-700",
  },
  {
    value: "organizing",
    label: "Em organização",
    className: "bg-amber-100 text-amber-800",
  },
  {
    value: "ready",
    label: "Pronto para apresentar",
    className: "bg-emerald-100 text-emerald-800",
  },
  {
    value: "not_applicable",
    label: "Não se aplica",
    className: "bg-blue-100 text-blue-800",
  },
  {
    value: "needs_guidance",
    label: "Requer orientação",
    className: "bg-rose-100 text-rose-800",
  },
];

const CATEGORY_LABELS = {
  identification: "Identificação",
  documents: "Documentos e registros",
  structure: "Estrutura e ambiente",
  clinical: "Prontuários e consentimentos",
  radiology: "Radiologia",
  sterilization: "Esterilização",
  waste: "Resíduos",
  equipment: "Equipamentos e materiais",
  people: "Equipe",
  advertising: "Publicidade",
  ethics: "Ética e sigilo",
};

const fileSize = (value) => {
  const bytes = Number(value || 0);
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const CFOInspectionPreparationPanel = ({ canManage, onProfileReview }) => {
  const { showToast } = useToast();
  const fileInputRef = useRef(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [openCategories, setOpenCategories] = useState({});
  const [uploadTarget, setUploadTarget] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const response = await api.get("/compliance/inspection-preparation");
      setData(response.data);
    } catch (error) {
      showToast(
        error?.response?.data?.detail ||
          "Não foi possível carregar a preparação para fiscalização.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const groups = useMemo(() => {
    const result = {};
    (data?.checklist || []).forEach((item) => {
      if (!result[item.category]) result[item.category] = [];
      result[item.category].push(item);
    });
    return result;
  }, [data]);

  const updateStatus = async (item, status) => {
    try {
      const response = await api.patch(
        `/compliance/inspection-preparation/items/${item.id}`,
        {
          status,
          note: item.note || null,
        },
      );
      setData(response.data);
      showToast("Preparação para fiscalização atualizada.");
    } catch (error) {
      showToast(
        error?.response?.data?.detail || "Não foi possível atualizar o item.",
        "error",
      );
    }
  };

  const chooseEvidence = (item) => {
    setUploadTarget(item);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const uploadEvidence = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !uploadTarget) return;
    const formData = new FormData();
    formData.append("file", file);
    setUploading(true);
    try {
      await api.post(
        `/compliance/inspection-preparation/items/${uploadTarget.id}/evidence`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      showToast("Evidência incluída.");
      await load();
    } catch (error) {
      showToast(
        error?.response?.data?.detail ||
          "Não foi possível incluir a evidência.",
        "error",
      );
    } finally {
      setUploading(false);
      setUploadTarget(null);
    }
  };

  const openEvidence = async (evidence) => {
    try {
      const response = await api.get(
        `/compliance/inspection-preparation/evidence/${evidence.id}/file`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(response.data);
      window.open(url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      showToast(
        error?.response?.data?.detail || "Não foi possível abrir a evidência.",
        "error",
      );
    }
  };

  if (loading)
    return (
      <section className="rounded-2xl bg-white p-5 text-sm text-gray-500 shadow-sm">
        Carregando preparação para fiscalização...
      </section>
    );
  if (!data) return null;
  const nextItem = data.next_item;

  return (
    <section className="overflow-hidden rounded-2xl border border-indigo-100 bg-white shadow-sm">
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
        onChange={uploadEvidence}
      />
      <div className="p-5 sm:p-6">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
          <div className="flex items-start gap-3">
            <div className="rounded-xl bg-indigo-50 p-3 text-indigo-700">
              <SearchCheck className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">
                Novo Manual de Fiscalização CFO 2026
              </p>
              <h3 className="mt-1 font-semibold text-gray-900">
                Preparar fiscalização do CRO
              </h3>
              <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-500">
                Organize o que pode ser verificado pelo fiscal sem misturar esse
                roteiro ao plano sanitário da RDC.
              </p>
              {data.system_support_total > 0 && (
                <p className="mt-2 text-xs font-medium text-emerald-700">
                  O OdontoAssist já encontrou registros que podem ajudar em{" "}
                  {data.system_support_total} ponto(s) deste roteiro.
                </p>
              )}
            </div>
          </div>
          <div className="shrink-0 rounded-xl bg-indigo-50 px-4 py-3 text-center">
            <p className="text-xl font-bold text-indigo-700">
              {data.progress}%
            </p>
            <p className="text-xs text-indigo-600">
              {data.completed_items} de {data.total_items} revisados
            </p>
          </div>
        </div>

        <div className="mt-5 flex flex-col gap-3 rounded-xl border border-indigo-100 bg-indigo-50/50 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">
              Roteiro selecionado
            </p>
            <p className="mt-1 text-sm font-medium text-gray-900">
              {data.inspection_type_label}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              O manual não estabelece um novo prazo geral de adequação.
            </p>
          </div>
          {canManage && (
            <button
              type="button"
              onClick={onProfileReview}
              className="shrink-0 rounded-lg border border-indigo-200 bg-white px-3 py-2 text-xs font-semibold text-indigo-700"
            >
              Alterar perfil e roteiro
            </button>
          )}
        </div>

        {nextItem ? (
          <div className="mt-5 rounded-xl border border-dashed border-indigo-200 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">
              Próximo ponto para revisar
            </p>
            <h4 className="mt-2 font-semibold text-gray-900">
              {nextItem.title}
            </h4>
            <p className="mt-1 text-sm leading-6 text-gray-600">
              {nextItem.inspection_check}
            </p>
            <div className="mt-3 rounded-lg bg-gray-50 p-3">
              <p className="text-xs font-semibold text-gray-800">
                {nextItem.owner_label}
              </p>
              <p className="mt-1 text-xs leading-5 text-gray-600">
                {nextItem.owner_detail}
              </p>
              <p className="mt-1 text-xs leading-5 text-indigo-700">
                {nextItem.odontoassist_help}
              </p>
            </div>
            {(nextItem.system_support || []).length > 0 && (
              <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                <p className="text-xs font-semibold text-emerald-800">
                  O OdontoAssist encontrou
                </p>
                <div className="mt-2 space-y-2">
                  {nextItem.system_support.map((support, index) => (
                    <div key={`${support.source}-${index}`}>
                      <p className="text-xs font-medium text-emerald-900">
                        {support.label}
                      </p>
                      <p className="text-xs leading-5 text-emerald-800">
                        {support.detail}
                      </p>
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-xs leading-5 text-emerald-700">
                  Isso é uma ajuda para a revisão, não uma confirmação
                  automática de regularidade.
                </p>
              </div>
            )}
            {canManage && (
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => updateStatus(nextItem, "organizing")}
                  className="rounded-lg bg-amber-100 px-3 py-2 text-xs font-semibold text-amber-800"
                >
                  Começar organização
                </button>
                <button
                  type="button"
                  onClick={() => updateStatus(nextItem, "ready")}
                  className="rounded-lg bg-emerald-100 px-3 py-2 text-xs font-semibold text-emerald-800"
                >
                  Pronto para apresentar
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="mt-5 flex items-center gap-3 rounded-xl bg-emerald-50 p-4 text-emerald-800">
            <CheckCircle2 className="h-5 w-5" />
            <p className="text-sm font-medium">
              Todos os pontos aplicáveis foram revisados.
            </p>
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center justify-between border-t px-5 py-4 text-left text-sm font-semibold text-gray-700 hover:bg-gray-50 sm:px-6"
      >
        <span>
          {expanded
            ? "Ocultar roteiro completo"
            : "Ver roteiro completo do CFO"}
        </span>
        {expanded ? (
          <ChevronUp className="h-5 w-5 text-gray-400" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-400" />
        )}
      </button>
      {expanded && (
        <div className="border-t bg-gray-50/50 p-4 sm:p-5">
          <div className="space-y-3">
            {Object.entries(groups).map(([category, items]) => {
              const categoryOpen = Boolean(openCategories[category]);
              const reviewed = items.filter((item) =>
                ["ready", "not_applicable"].includes(item.status),
              ).length;
              return (
                <div
                  key={category}
                  className="overflow-hidden rounded-xl border bg-white"
                >
                  <button
                    type="button"
                    onClick={() =>
                      setOpenCategories({
                        ...openCategories,
                        [category]: !categoryOpen,
                      })
                    }
                    className="flex w-full items-center justify-between gap-3 p-4 text-left"
                  >
                    <div>
                      <p className="font-medium text-gray-900">
                        {CATEGORY_LABELS[category] || category}
                      </p>
                      <p className="mt-0.5 text-xs text-gray-500">
                        {reviewed} de {items.length} revisados
                      </p>
                    </div>
                    {categoryOpen ? (
                      <ChevronUp className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                  {categoryOpen && (
                    <div className="divide-y border-t">
                      {items.map((item) => {
                        const current =
                          STATUS_OPTIONS.find(
                            (option) => option.value === item.status,
                          ) || STATUS_OPTIONS[0];
                        return (
                          <div key={item.id} className="p-4">
                            <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
                              <div className="min-w-0">
                                <h5 className="font-medium text-gray-900">
                                  {item.title}
                                </h5>
                                <p className="mt-1 text-sm leading-6 text-gray-600">
                                  <strong>O fiscal pode verificar:</strong>{" "}
                                  {item.inspection_check}
                                </p>
                                <p className="mt-1 text-sm leading-6 text-gray-500">
                                  <strong>Como preparar:</strong>{" "}
                                  {item.preparation}
                                </p>
                                <div className="mt-2 rounded-lg bg-gray-50 p-2.5 text-xs leading-5 text-gray-600">
                                  <strong>{item.owner_label}.</strong>{" "}
                                  {item.owner_detail}
                                  <span className="mt-1 block text-indigo-700">
                                    {item.odontoassist_help}
                                  </span>
                                </div>
                                {(item.system_support || []).length > 0 && (
                                  <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 p-2.5">
                                    <p className="text-xs font-semibold text-emerald-800">
                                      Já encontrado no OdontoAssist
                                    </p>
                                    {(item.system_support || []).map(
                                      (support, index) => (
                                        <p
                                          key={`${support.source}-${index}`}
                                          className="mt-1 text-xs leading-5 text-emerald-800"
                                        >
                                          <strong>{support.label}:</strong>{" "}
                                          {support.detail}
                                        </p>
                                      ),
                                    )}
                                  </div>
                                )}
                              </div>
                              {canManage ? (
                                <select
                                  value={item.status}
                                  onChange={(event) =>
                                    updateStatus(item, event.target.value)
                                  }
                                  className="shrink-0 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs"
                                >
                                  {STATUS_OPTIONS.map((option) => (
                                    <option
                                      key={option.value}
                                      value={option.value}
                                    >
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <span
                                  className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium ${current.className}`}
                                >
                                  {current.label}
                                </span>
                              )}
                            </div>
                            <div className="mt-3 flex flex-wrap items-center gap-2">
                              {(item.evidence || []).map((evidence) => (
                                <button
                                  key={evidence.id}
                                  type="button"
                                  onClick={() => openEvidence(evidence)}
                                  className="flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs text-gray-600"
                                >
                                  <Download className="h-3.5 w-3.5" />{" "}
                                  {evidence.original_filename} ·{" "}
                                  {fileSize(evidence.file_size)}
                                </button>
                              ))}
                              {canManage && (
                                <button
                                  type="button"
                                  disabled={uploading}
                                  onClick={() => chooseEvidence(item)}
                                  className="flex items-center gap-1.5 rounded-lg border border-indigo-200 px-2.5 py-1.5 text-xs font-medium text-indigo-700 disabled:opacity-50"
                                >
                                  {uploading && uploadTarget?.id === item.id ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  ) : (
                                    <FileUp className="h-3.5 w-3.5" />
                                  )}{" "}
                                  Anexar evidência
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div className="border-t bg-white p-4 sm:px-6">
        <div className="flex items-start gap-2 text-xs leading-5 text-gray-500">
          <ShieldQuestion className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600" />
          <span>
            {data.disclaimer}{" "}
            <a
              href={data.manual_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 font-medium text-indigo-700"
            >
              Consultar fonte oficial <ExternalLink className="h-3 w-3" />
            </a>
          </span>
        </div>
        <div className="mt-3 flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-xs leading-5 text-amber-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> Não anexe
          prontuários nem dados identificáveis de pacientes como evidência deste
          checklist.
        </div>
      </div>
    </section>
  );
};

export default CFOInspectionPreparationPanel;
